import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const STORAGE_KEY = "rgm_whatsnew_v3_2_dismissed";

// Routes where the toast must NOT render — high-attention surfaces
// where a bottom-anchored card competes with the page's primary CTA
// (Phase 20.6 — RGM Shuffle Radio).
const HIDE_ROUTES = ["/shuffle", "/studio/session"];

const HIGHLIGHTS = [
  { icon: "👤", title: "New Profile Tabs", body: "Tracks · Features · Playlists · Spotlight Rank" },
  { icon: "💬", title: "Replies + Emoji Reactions", body: "Threaded comments with a full emoji picker" },
  { icon: "📡", title: "Live Scheduling + Join Live", body: "YouTube · Facebook · Instagram · TikTok · Custom" },
  { icon: "🔔", title: "Feature & Spotlight Pings", body: "Primary artist + every tagged collaborator" },
];

/**
 * Phase 3.2 — Small dismissible "What's New" card.
 * Shows once per logged-in user (until they click "Got it").
 * Anonymous visitors never see it.
 */
const WhatsNewToast = () => {
  const [show, setShow] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;
    // Only surface to logged-in users (avoid disrupting anonymous landing visitors)
    api.get("/auth/me")
      .then((r) => {
        if (cancelled) return;
        if (r?.data?.user_id) {
          // Brief delay so it appears after the page settles
          setTimeout(() => !cancelled && setShow(true), 900);
        }
      })
      .catch(() => { /* not logged in — silently ignore */ });
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setShow(false);
  };

  if (!show) return null;
  if (HIDE_ROUTES.some((r) => location.pathname.startsWith(r))) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="whats-new-toast"
      className="fixed bottom-5 right-5 z-50 w-[330px] max-w-[calc(100vw-2rem)] rounded-2xl border border-purple-500/40 shadow-2xl backdrop-blur-md"
      style={{
        background: "linear-gradient(135deg, rgba(26,16,42,0.95) 0%, rgba(16,12,30,0.95) 100%)",
        animation: "rgmToastIn 380ms cubic-bezier(0.2, 0.9, 0.3, 1.2) both",
      }}
    >
      <style>{`
        @keyframes rgmToastIn {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-purple-400 font-semibold">What's New</p>
            <p className="text-white text-base font-bold leading-tight">RGM Studio v3.2</p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Close"
            data-testid="whats-new-close"
            className="text-gray-500 hover:text-white transition-colors -mr-1 -mt-1 p-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <ul className="space-y-2 mb-3">
          {HIGHLIGHTS.map((h) => (
            <li key={h.title} className="flex items-start gap-2.5">
              <span className="text-base leading-none mt-0.5" aria-hidden>{h.icon}</span>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium leading-tight">{h.title}</p>
                <p className="text-gray-400 text-xs leading-snug">{h.body}</p>
              </div>
            </li>
          ))}
        </ul>

        <button
          onClick={dismiss}
          data-testid="whats-new-got-it"
          className="w-full py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 transition-all"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

export default WhatsNewToast;
